<template>
	<nk-page :commercialAccess="['Salary']" class="" policyClaims="Salary_CooperateType_Management">
		<template #badges>
			<nk-badge system="salary">
				<nk-badge-item type="system" modelValue="salary"></nk-badge-item>
				<nk-badge-item type="system" modelValue="salary" :title="$t('forms.CooperateType')"></nk-badge-item>
			</nk-badge>
		</template>
		<div class="nk-card h-100 d-flex flex-column" startup>
			<div class="h-grow-lock">
				<nk-table v-model="selectedRow" @data-changed="dataChanged" :data="cooperateTypes" :options="tableOptions" ref="cooperateTypes" tabindex="101" @row_dbClicked="rowDbClicked">
				</nk-table>
			</div>
		</div>
	</nk-page>
</template>
<script lang="ts">
	import Input from '~/components/general/nikan-input.vue'
	import $sc from '~/plugins/shortcut.collection.js'
	import dialog from '~/plugins/nikan-dialog-plugin'
	import validators from '~/statics/validators'
	import { defineComponent } from 'vue'
	import { TaminBranch } from '@/classes/salary/tamin-branch'
	import { post } from '~/plugins/nikan-api'
	import Endpoints from '@nikan/endpoints'
	import { IResponseList } from '@/interfaces/base'
	import IsEmpty from 'lodash/isEmpty'
	import { TYPE } from 'vue-toastification'
	import { CooperateType } from '@/classes/salary/cooperate-type'

	export default defineComponent({
		modal: {
			size: 'custom',
			width: 900,
			height: 600,
			stateUID: 'cooperate-type-selector',
		},
		props: {
			options: { type: Object },
		},
		data() {
			return {
				cooperateTypes: [] as CooperateType[],
				selectedRow: null as CooperateType | null,
				tableOptions: {
					activeRowDisabled: true,
					editable: this.$nikan.checkPermission({ or: ['Salary_CooperateType_Add', 'Salary_CooperateType_Edit'] }),
					searchable: false,
					allowNewRow: true,
					alwaysEmit: true,
					buttons: {
						addBtn: {
							options: {
								visible: true,
								policyClaims: 'Salary_CooperateType_Add',
							}
						},
						deleteBtn: {
							options: {
								visible: true,
								policyClaims: 'Salary_CooperateType_Delete',
							}
						}
					},
					displayOrderEnabled: false,
					prepareNewRow: () => new TaminBranch(),
					columns: [
						{
							name: 'rowNumber',
							title: this.$t('row-number'),
							searchable: true,
							sortable: true,
							editable: false,
							width: 20,
							entering: false,
							pinned: true,
							pinnable: false,
							resizable: false,
						},
						{
							name: 'code',
							title: this.$t('salary.cooperate-type.f.code'),
							edit: {
								component: Input,
								options: {
									mode: 'strnumber',
								},
							},
							searchable: true,
							sortable: true,
							editable: true,
							width: 40,
							entering: true,
							pinnable: true,
							validators: validators('required'),
						},
						{
							name: 'name',
							title: this.$t('salary.cooperate-type.f.name'),
							edit: {
								component: Input,
								options: {
									mode: 'string',
								},
							},
							searchable: true,
							sortable: false,
							editable: true,
							width: 150,
							validators: validators('required'),
						},
					],
				},
				pageButtons: {
					saveButton: {
						title: this.$t('save'),
						shortcut: $sc.find('save'),
						icon: 'save',
						visible: true,
						disabled: true,
						isPrimary: true,
						handle: () => {
							this.save().then(() => {
								this.$nikan.formUtility.switchMode(this.pageButtons, 'select',)
								this.$refs.cooperateTypes.table_Focused()
							})
						},
						policyClaims: { or: ['Salary_CooperateType_Add', 'Salary_CooperateType_Edit', 'Salary_CooperateType_Delete'] },
					},
					selectButton: {
						title: this.$t('select'),
						shortcut: $sc.find('save'),
						icon: 'check',
						isPrimary: true,
						visible: true,
						disabled: false,
						handle: () => {
							if (IsEmpty(this.selectedRow))
								return this.$toast(this.$t('document.m.select-row'))
							this.resolve(this.selectedRow)
						}
					}
				}
			}
		},
		mounted() {
			this.prepareWindow(this.$t('salary.cooperate-type.list-title'), [this.pageButtons.selectButton, this.pageButtons.saveButton])
			this.loadCooperateTypes()
		},
		methods: {
			save(): Promise<CooperateType[]> {
				return new Promise((resolve, reject) => {
					this.$refs.cooperateTypes
						.commitChanges()
						.then((cooperateTypes: (CooperateType & { rowState: string })[]) => {
							const wait = this.$wait(this.$el)
							post(Endpoints.getUrl('cooperateType.modify'), { items: cooperateTypes })
								.then(() => {
									this.$toast(this.$t('salary.cooperate-type.m.save-success'), { type: TYPE.SUCCESS })
									this.loadCooperateTypes()
									resolve(cooperateTypes)
								})
								.finally(() => {
									wait.end()
								})
						})
						.catch((errorRows: { message: string } | { rowNumber: number }[]) => {
							if (Array.isArray(errorRows)) {
								var rowNumbers = errorRows.map((x, i) => x.rowNumber).join(',')
								dialog({
									title: this.$t('salary.tamin-branch.m.save-branch-title'),
									body: this.$t('salary.tamin-branch.m.error-rows', { rowNumbers }),
									type: 'error',
									isPrimary: true,
								})
							} else {
								dialog({
									title: this.$t('salary.tamin-branch.m.save-branch-title'),
									body: errorRows.message,
									type: 'error',
									isPrimary: true,
								})
							}
							reject(new Error('invalid-rows'))
							this.defer(() => this.$refs.taminBranchList.$el.focus(), 100)
						})
				})
			},
			dataChanged() {
				this.$nikan.formUtility.switchMode(this.pageButtons, 'edit')
			},
			rowDbClicked(val: TaminBranch): void {
				if (this.grandParentType() === 'modal')
					this.resolve(val)
			},
			loadCooperateTypes(): void {
				const wait = this.$wait(this.$el)
				post(Endpoints.getUrl('cooperateType.listDynamic'))
					.then((res: IResponseList<CooperateType>) => {
						this.cooperateTypes = res.data.items
					})
					.finally(() => {
						wait.end()
					})
			}
		},
	})
</script>
<style></style>
